/**
 * UpdateNotification Component
 * 
 * Displays a notification when a new app version is available.
 * Users can refresh immediately or dismiss and continue working.
 */

import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  Slide,
  useTheme,
  useMediaQuery
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import mixpanel from 'mixpanel-browser';
import VersionCheckService from '../../services/VersionCheckService';
import { VERSION_CHECK_CONFIG } from '../../config/versionCheck';
import { r } from '../../theme/tokens';

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const UpdateNotification = () => {
  const [open, setOpen] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Register callback for when update is detected
    VersionCheckService.onUpdateAvailable((result) => {
      if (VERSION_CHECK_CONFIG.debugMode) {
        console.log('[UpdateNotification] Update detected:', result);
      }
      setUpdateInfo(result);
      setOpen(true);

      // Track analytics
      try {
        mixpanel.track('update_notification_shown', {
          current_version: VersionCheckService.currentHash,
          new_version: result.newHash,
          page: window.location.pathname
        });
      } catch (error) {
        console.error('[UpdateNotification] Mixpanel error:', error);
      }
    });

    // Start checking for updates
    VersionCheckService.startChecking();

    // Cleanup on unmount
    return () => {
      VersionCheckService.stopChecking();
    };
  }, []); // Empty dependency array = runs once on mount

  const handleRefresh = () => {
    // Track analytics
    try {
      mixpanel.track('update_notification_refresh_clicked', {
        current_version: VersionCheckService.currentHash,
        new_version: updateInfo?.newHash,
        page: window.location.pathname
      });
    } catch (error) {
      console.error('[UpdateNotification] Mixpanel error:', error);
    }

    // Refresh the page
    VersionCheckService.refreshApp();
  };

  const handleDismiss = () => {
    setOpen(false);

    // Track analytics
    try {
      mixpanel.track('update_notification_dismissed', {
        current_version: VersionCheckService.currentHash,
        new_version: updateInfo?.newHash,
        page: window.location.pathname
      });
    } catch (error) {
      console.error('[UpdateNotification] Mixpanel error:', error);
    }
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{
        bottom: { xs: 70, md: 24 }, // Account for mobile bottom nav
      }}
    >
      <Alert
        severity="info"
        icon={false}
        action={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1 }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleRefresh}
              startIcon={<RefreshIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 'auto',
                px: 1.5,
                py: 0.5
              }}
            >
              Refresh
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          width: 'auto',
          maxWidth: { xs: '90vw', sm: r(500) },
          py: 0.5,
          px: 1.5,
          '& .MuiAlert-message': {
            py: 0.5,
            display: 'flex',
            alignItems: 'center'
          },
          '& .MuiAlert-action': {
            alignItems: 'center',
            padding: 0,
            marginRight: 0
          },
          '& .MuiAlert-icon': {
            padding: 0,
            marginRight: 1,
            alignItems: 'center'
          }
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
          New version available
        </Typography>
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification;

